import { useEffect, useState, useRef, useCallback } from 'react';
import { apiGet, apiPost, apiPatch, apiDelete, ApiClientError } from '@client/lib/api';
import { formatCents, parseCents } from '@shared/money';
import styles from './ServicesPage.module.css';

/** A service catalog row as returned by GET /api/v1/services. */
interface Service {
  id: string;
  name: string;
  description: string | null;
  defaultPriceCents: number;
  defaultDurationMinutes: number;
  isActive: boolean;
}

/** The server response after creating a service: the full new row. */
type CreatedService = Service;

/** The server response after PATCHing a service: the full updated row. */
type UpdatedService = Service;

type ViewState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'loaded'; services: Service[] };

/**
 * The Services catalog screen.
 *
 * Lists the services the owner sells (e.g. "Mow & edge, standard lot"), each
 * with a default price and duration. Jobs and quotes are priced from these.
 *
 * - GET /api/v1/services lists active services.
 * - POST /api/v1/services creates one.
 * - PATCH /api/v1/services/:id updates one.
 * - DELETE /api/v1/services/:id archives one (soft delete).
 *
 * NO OPTIMISTIC UPDATES: a row does not change until the server confirms.
 * While a save is in flight the control is disabled and shows pending; on
 * success the screen re-renders from the server's response; on failure the
 * row keeps its previous value and the server's message is shown.
 *
 * MONEY: defaultPriceCents is integer cents. The owner sees a dollar amount
 * and their input is parsed with parseCents. Prices are always displayed with
 * formatCents.
 */
export function ServicesPage() {
  const [state, setState] = useState<ViewState>({ kind: 'loading' });

  /** Whether the "new service" form is open. */
  const [showForm, setShowForm] = useState(false);
  /** Draft field values for the new-service form. */
  const [form, setForm] = useState({
    name: '',
    description: '',
    price: '',
    duration: '',
  });
  /** Whether a create is in flight. */
  const [creating, setCreating] = useState(false);
  /** Error message for a failed create, or null. */
  const [createError, setCreateError] = useState<string | null>(null);

  /** id of the service currently being saved, or null. */
  const [savingId, setSavingId] = useState<string | null>(null);
  /** Error message for a failed save, or null. */
  const [saveError, setSaveError] = useState<string | null>(null);

  /** Whether archived services are shown. */
  const [showArchived, setShowArchived] = useState(false);

  /**
   * The committed services — the value from the last server response. Kept in a
   * ref so that save handlers always see the committed value even after the
   * user has typed into a field (which updates `state`).
   */
  const committedRef = useRef<Service[] | null>(null);

  const fetchServices = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const data = await apiGet<Service[]>('/api/v1/services');
      committedRef.current = data;
      setState({ kind: 'loaded', services: data });
    } catch (err) {
      setState({
        kind: 'error',
        message: err instanceof ApiClientError ? err.message : 'Could not load services.',
      });
    }
  }, []);

  useEffect(() => {
    void fetchServices();
  }, [fetchServices]);

  const handleFieldChange =
    (field: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

  const isFormEmpty = !form.name.trim();

  const handleCreate = useCallback(async () => {
    // Parse the price client-side. If it is not a valid dollar amount, show
    // the error and send NO request.
    const priceCents = parseCents(form.price);
    if (priceCents === null) {
      setCreateError('Enter a valid price, like $45.00.');
      return;
    }

    // Duration must be a whole number of minutes.
    const duration = Number(form.duration);
    if (!Number.isInteger(duration) || duration < 0) {
      setCreateError('Duration must be a whole number of minutes.');
      return;
    }

    setCreating(true);
    setCreateError(null);
    try {
      const created = await apiPost<CreatedService>('/api/v1/services', {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        defaultPriceCents: priceCents,
        defaultDurationMinutes: duration,
      });
      // Re-render from the SERVER's response: prepend the confirmed row.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return { kind: 'loaded', services: [created, ...prev.services] };
      });
      committedRef.current =
        committedRef.current === null ? [created] : [created, ...committedRef.current];
      setShowForm(false);
      setForm({ name: '', description: '', price: '', duration: '' });
    } catch (err) {
      // On failure: keep the form open, leave the list unchanged, surface the
      // server's message.
      setCreateError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not create this service. Please try again.'
      );
    } finally {
      setCreating(false);
    }
  }, [form]);

  /**
   * Save a single field of a service.
   *
   * NO OPTIMISTIC UPDATE: revert the displayed value to the committed value
   * immediately, before the request resolves. The input is disabled while the
   * save is in flight. Only the server's response will update the displayed
   * value.
   */
  const saveField = useCallback(
    async (service: Service, field: keyof Service, newValue: unknown) => {
      const committed = committedRef.current ?? [];
      const committedRow = committed.find((s) => s.id === service.id) ?? service;
      const oldValue = committedRow[field];

      // Revert the displayed value to the committed value immediately.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          services: prev.services.map((s) =>
            s.id === service.id ? { ...s, [field]: oldValue } : s
          ),
        };
      });
      setSavingId(service.id);
      setSaveError(null);
      try {
        const updated = await apiPatch<UpdatedService>(`/api/v1/services/${service.id}`, {
          [field]: newValue,
        });
        // Re-render from the SERVER's response.
        committedRef.current = (committedRef.current ?? []).map((s) =>
          s.id === service.id ? updated : s
        );
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            services: prev.services.map((s) => (s.id === service.id ? updated : s)),
          };
        });
      } catch (err) {
        // On failure: the committed value is already shown. Surface the
        // server's message.
        setSaveError(
          err instanceof ApiClientError
            ? err.message
            : 'Could not save this change. Please try again.'
        );
      } finally {
        setSavingId(null);
      }
    },
    []
  );

  const handleNameChange = (service: Service) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (state.kind !== 'loaded') return;
    setState({
      kind: 'loaded',
      services: state.services.map((s) =>
        s.id === service.id ? { ...s, name: e.target.value } : s
      ),
    });
  };

  const handleNameBlur = useCallback(
    (service: Service) => () => {
      if (state.kind !== 'loaded') return;
      const row = state.services.find((s) => s.id === service.id);
      if (!row) return;
      void saveField(service, 'name', row.name);
    },
    [state, saveField]
  );

  const handlePriceChange = (service: Service) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (state.kind !== 'loaded') return;
    // Display the raw dollar string the owner is typing. The committed cents
    // value is preserved in committedRef for revert on blur / failure.
    setState({
      kind: 'loaded',
      services: state.services.map((s) =>
        s.id === service.id ? { ...s, defaultPriceCents: e.target.value as unknown as number } : s
      ),
    });
  };

  const handlePriceBlur = useCallback(
    (service: Service) => () => {
      if (state.kind !== 'loaded') return;
      const row = state.services.find((s) => s.id === service.id);
      if (!row) return;
      const displayValue = row.defaultPriceCents as unknown as string;
      const committed = committedRef.current ?? [];
      const committedRow = committed.find((s) => s.id === service.id) ?? service;

      // Parse the price client-side. If it is not a valid dollar amount, show
      // the error and send NO request.
      const priceCents = parseCents(displayValue);
      if (priceCents === null || priceCents < 0) {
        // Revert to the committed value without sending a request.
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            services: prev.services.map((s) =>
              s.id === service.id ? { ...s, defaultPriceCents: committedRow.defaultPriceCents } : s
            ),
          };
        });
        setSaveError('Enter a valid price, like $45.00.');
        return;
      }

      void saveField(service, 'defaultPriceCents', priceCents);
    },
    [state, saveField]
  );

  const handleDurationChange = (service: Service) => (e: React.ChangeEvent<HTMLInputElement>) => {
    if (state.kind !== 'loaded') return;
    setState({
      kind: 'loaded',
      services: state.services.map((s) =>
        s.id === service.id
          ? {
              ...s,
              defaultDurationMinutes: e.target.value as unknown as number,
            }
          : s
      ),
    });
  };

  const handleDurationBlur = useCallback(
    (service: Service) => () => {
      if (state.kind !== 'loaded') return;
      const row = state.services.find((s) => s.id === service.id);
      if (!row) return;
      const displayValue = row.defaultDurationMinutes as unknown as string;
      const committed = committedRef.current ?? [];
      const committedRow = committed.find((s) => s.id === service.id) ?? service;

      // Duration must be a whole number of minutes.
      const duration = Number(displayValue);
      if (!Number.isInteger(duration) || duration < 0) {
        // Revert to the committed value without sending a request.
        setState((prev) => {
          if (prev.kind !== 'loaded') return prev;
          return {
            kind: 'loaded',
            services: prev.services.map((s) =>
              s.id === service.id
                ? {
                    ...s,
                    defaultDurationMinutes: committedRow.defaultDurationMinutes,
                  }
                : s
            ),
          };
        });
        setSaveError('Duration must be a whole number of minutes.');
        return;
      }

      void saveField(service, 'defaultDurationMinutes', duration);
    },
    [state, saveField]
  );

  const handleArchive = useCallback(async (service: Service) => {
    if (
      !window.confirm(
        `Archive "${service.name}"? Past jobs keep the price they were quoted, so this won't change any existing job, quote or invoice.`
      )
    ) {
      return;
    }
    setSavingId(service.id);
    setSaveError(null);
    try {
      await apiDelete(`/api/v1/services/${service.id}`);
      // Re-render from the SERVER's response: remove the archived row.
      setState((prev) => {
        if (prev.kind !== 'loaded') return prev;
        return {
          kind: 'loaded',
          services: prev.services.filter((s) => s.id !== service.id),
        };
      });
      committedRef.current = (committedRef.current ?? []).filter((s) => s.id !== service.id);
    } catch (err) {
      setSaveError(
        err instanceof ApiClientError
          ? err.message
          : 'Could not archive this service. Please try again.'
      );
    } finally {
      setSavingId(null);
    }
  }, []);

  if (state.kind === 'loading') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} aria-live="polite">
          Loading services…
        </p>
      </main>
    );
  }

  if (state.kind === 'error') {
    return (
      <main id="main" className={styles.container}>
        <p className={styles.status} role="alert">
          {state.message}
        </p>
        <button type="button" className={styles.retryButton} onClick={() => void fetchServices()}>
          Retry
        </button>
      </main>
    );
  }

  const { services } = state;

  return (
    <main id="main" className={styles.container}>
      <header className={styles.header}>
        <h1 className={styles.title}>Services</h1>
        <button type="button" className={styles.newButton} onClick={() => setShowForm((s) => !s)}>
          {showForm ? 'Cancel' : 'New service'}
        </button>
      </header>

      <section className={styles.toggleSection} aria-label="Archived services">
        <label className={styles.toggle}>
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
          />
          <span className={styles.toggleLabel}>Show archived services</span>
        </label>
      </section>

      {showForm && (
        <section className={styles.formSection} aria-label="New service">
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="serviceName">
              Name
            </label>
            <input
              id="serviceName"
              type="text"
              className={styles.input}
              value={form.name}
              onChange={handleFieldChange('name')}
              disabled={creating}
              aria-label="Name"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="serviceDescription">
              Description
            </label>
            <input
              id="serviceDescription"
              type="text"
              className={styles.input}
              value={form.description}
              onChange={handleFieldChange('description')}
              disabled={creating}
              aria-label="Description"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="servicePrice">
              Default price
            </label>
            <input
              id="servicePrice"
              type="text"
              className={styles.input}
              value={form.price}
              onChange={handleFieldChange('price')}
              disabled={creating}
              aria-label="Default price"
              placeholder="$45.00"
            />
          </div>
          <div className={styles.field}>
            <label className={styles.fieldLabel} htmlFor="serviceDuration">
              Default duration (minutes)
            </label>
            <input
              id="serviceDuration"
              type="number"
              className={styles.input}
              value={form.duration}
              onChange={handleFieldChange('duration')}
              disabled={creating}
              aria-label="Default duration in minutes"
              placeholder="60"
            />
          </div>
          {createError && (
            <p className={styles.fieldError} role="alert">
              {createError}
            </p>
          )}
          <button
            type="button"
            className={styles.saveButton}
            disabled={creating || isFormEmpty}
            aria-busy={creating}
            onClick={() => void handleCreate()}
          >
            {creating ? 'Saving…' : 'Save'}
          </button>
        </section>
      )}

      {services.length === 0 ? (
        <p className={styles.empty}>No services yet. Add one to get started.</p>
      ) : (
        <ul className={styles.serviceList}>
          {services.map((service) => {
            const isSaving = savingId === service.id;
            const priceDisplay =
              typeof service.defaultPriceCents === 'number' &&
              Number.isFinite(service.defaultPriceCents)
                ? formatCents(service.defaultPriceCents)
                : '';
            const durationDisplay =
              typeof service.defaultDurationMinutes === 'number' &&
              Number.isFinite(service.defaultDurationMinutes)
                ? String(service.defaultDurationMinutes)
                : '';
            return (
              <li key={service.id} className={styles.serviceRow}>
                <div className={styles.serviceInfo}>
                  <span className={styles.fieldLabel}>Name</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={service.name}
                    onChange={handleNameChange(service)}
                    onBlur={handleNameBlur(service)}
                    disabled={isSaving}
                    aria-busy={isSaving}
                    aria-label={`Name for ${service.name}`}
                  />
                  <span className={styles.fieldLabel}>Description</span>
                  <span className={styles.description}>{service.description ?? '—'}</span>
                  <span className={styles.fieldLabel}>Default price</span>
                  <input
                    type="text"
                    className={styles.input}
                    value={priceDisplay}
                    onChange={handlePriceChange(service)}
                    onBlur={handlePriceBlur(service)}
                    disabled={isSaving}
                    aria-busy={isSaving}
                    aria-label={`Default price for ${service.name}`}
                  />
                  <span className={styles.fieldLabel}>Default duration</span>
                  <input
                    type="number"
                    className={styles.input}
                    value={durationDisplay}
                    onChange={handleDurationChange(service)}
                    onBlur={handleDurationBlur(service)}
                    disabled={isSaving}
                    aria-busy={isSaving}
                    aria-label={`Default duration for ${service.name}`}
                  />
                </div>
                <button
                  type="button"
                  className={styles.archiveButton}
                  onClick={() => void handleArchive(service)}
                  disabled={isSaving}
                  aria-label={`Archive ${service.name}`}
                >
                  Archive
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <section className={styles.note} aria-label="Pricing note">
        <p className={styles.noteText}>
          Editing a catalog price never changes an existing job, quote or invoice — past jobs keep
          the price they were quoted. Archiving a service removes it from this list but keeps the
          row, because past jobs still reference it.
        </p>
      </section>

      {saveError && (
        <p className={styles.saveError} role="alert">
          {saveError}
        </p>
      )}
    </main>
  );
}
